import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { format } from 'date-fns';
import { parseUtcTimestamp } from '../../../Helpers/utils';

/**
 * AuditTrailDisplay Component
 * 
 * Displays the audit trail for a change in chronological order.
 * Shows username, timestamp, and comment text for each entry.
 * Visually distinguishes the initial comment (creation entry).
 * 
 * @param {Object} props
 * @param {Array} props.auditTrail - Array of audit trail entries
 */
const AuditTrailDisplay = ({ auditTrail }) => {
  if (!auditTrail || auditTrail.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">No comments or history yet</Typography>
      </Box>
    );
  }

  const formatTimestamp = (timestamp) => {
    try {
      const date = parseUtcTimestamp(timestamp);
      if (!date) return 'Invalid date';
      return format(date, 'MMM dd, yyyy hh:mm a');
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid date';
    }
  };

  const getEntryDisplay = (entry) => {
    const firstName = entry.user?.first_name || '';
    const lastName = entry.user?.last_name || '';
    const userName = `${firstName} ${lastName}`.trim() || 'Unknown User';
    const entryType = entry.entry_type?.toUpperCase();
    
    switch (entryType) {
      case 'CREATION':
        return {
          action: `${userName} created this change`,
          comment: entry.comment_text,
          borderColor: 'success.main' // Green for creation
        };
      case 'EDIT':
        return {
          action: `${userName} edited this change`,
          comment: null,
          borderColor: 'warning.main' // Orange for edit
        };
      case 'COMMENT':
        return {
          action: userName, // Just show the user name for comments
          comment: entry.comment_text,
          borderColor: 'info.main' // Blue for comment
        };
      default:
        return {
          action: `${userName} performed an action`,
          comment: entry.comment_text,
          borderColor: 'grey.400' // Grey for unknown
        };
    }
  };

  return (
    <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
      {auditTrail.map((entry) => {
        const display = getEntryDisplay(entry);
        const timestamp = formatTimestamp(entry.timestamp);
        
        return (
          <Paper
            key={entry.id}
            elevation={1}
            sx={{
              p: 2,
              mb: 1,
              backgroundColor: 'background.paper',
              borderLeft: '4px solid',
              borderColor: display.borderColor
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight="bold">
                {display.action}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {timestamp}
              </Typography>
            </Box>
            {display.comment && (
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                {display.comment}
              </Typography>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

export default AuditTrailDisplay;
